import React, { useEffect, useMemo, useState } from "react";
import {
	Area,
	AreaChart,
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	ComposedChart,
	Legend,
	Line,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { getDashboardAnalytics } from "../../api/apiServiceFetch";
import useAdminRealtimeTick from "../../hooks/useAdminRealtimeTick";
import WorldMap from "./WorldMap";
import "./Home.scss";

const COLORS = ["#2563eb", "#db2777", "#f59e0b", "#16a34a", "#7c3aed"];

const pick = (obj, ...keys) => {
	for (const key of keys) {
		if (obj?.[key] !== undefined && obj?.[key] !== null) return obj[key];
	}

	return 0;
};

const toNumber = (value) => Number(value || 0);

const money = (value) =>
	new Intl.NumberFormat("ru-RU", {
		maximumFractionDigits: 0,
	}).format(toNumber(value));

const normalizePoint = (item) => ({
	date: item.date || item.Date || "",
	count: toNumber(item.count ?? item.Count),
	delCount: toNumber(item.delCount ?? item.DelCount),
	modCount: toNumber(item.modCount ?? item.ModCount),
	blockCount: toNumber(item.blockCount ?? item.BlockCount),
	users: toNumber(item.users ?? item.Users),
	totalRub: toNumber(item.totalRub ?? item.TotalRub),
	rubs: toNumber(item.rubs ?? item.Rubs),
	stars: toNumber(item.stars ?? item.Stars),
});

const segmentLabels = {
	waiting: "Ожидают модерации",
	all: "Все пользователи",
	active: "Активные",
	failed: "Не заполнены",
	deleted: "Удаленные",
	edit: "Изменения",
	blocked: "Заблокированные",
};

const segmentOrder = [
	"waiting",
	"all",
	"active",
	"failed",
	"deleted",
	"edit",
	"blocked",
];

const kpis = (stats) => [
	{
		label: "Активные пользователи",
		value: pick(stats, "activeAll", "ActiveAll"),
		delta: pick(stats, "percent_Change_Week", "Percent_Change_Week"),
	},
	{
		label: "MAU за 30 дней",
		value: pick(stats, "mau_30D", "Mau_30D", "mau_30d"),
		delta: pick(stats, "percent_Change_Week", "Percent_Change_Week"),
	},
	{
		label: "DAU сегодня",
		value: pick(stats, "dau_Today", "Dau_Today"),
		delta: pick(stats, "percent_Change_Day", "Percent_Change_Day"),
	},
	{
		label: "Новые за 7 дней",
		value: pick(stats, "new_7Days", "New_7Days"),
		delta: pick(stats, "percent_Change_Day", "Percent_Change_Day"),
	},
	{
		label: "Оплаты сегодня",
		value: `${money(pick(stats, "rubs", "Rubs"))} RUB`,
		delta: pick(
			stats,
			"percent_Change_RubsThisMonth",
			"Percent_Change_RubsThisMonth",
		),
		subtitle: `За всё время: ${money(pick(stats, "totalAllTime_In_Rub", "TotalAllTime_In_Rub"))} RUB`,
	},
	{
		label: "Доход за месяц",
		value: `${money(pick(stats, "rubsThisMonth", "RubsThisMonth"))} RUB`,
		delta: pick(
			stats,
			"percent_Change_RubsThisMonth",
			"Percent_Change_RubsThisMonth",
		),
		subtitle: `Прошлый месяц: ${money(pick(stats, "rubsLastMonth", "RubsLastMonth"))} RUB`,
	},
	{
		label: "Stars за месяц",
		value: money(pick(stats, "starsThisMonth", "StarsThisMonth")),
		delta: pick(
			stats,
			"percent_Change_StarsThisMonth",
			"Percent_Change_StarsThisMonth",
		),
		subtitle: `Прошлый месяц: ${money(pick(stats, "starsLastMonth", "StarsLastMonth"))}`,
	},
	{
		label: "Premium активные",
		value: pick(stats, "activePremium", "ActivePremium"),
		delta: pick(stats, "percent_Change_Premium", "Percent_Change_Premium"),
	},
];

const normalizeSegment = (item) => ({
	key: item.key || item.Key || "",
	total: toNumber(item.total ?? item.Total),
	male: toNumber(item.male ?? item.Male),
	female: toNumber(item.female ?? item.Female),
});

const normalizeGenderName = (name) => {
	if (name === "Male") return "Мужчины";
	if (name === "Female") return "Женщины";
	return "Не указано";
};

const Delta = ({ value }) => {
	if (value === null || value === undefined || Number.isNaN(Number(value))) {
		return <span className="home__delta home__delta--muted">0%</span>;
	}

	const numeric = Number(value);
	const className =
		numeric >= 0
			? "home__delta home__delta--up"
			: "home__delta home__delta--down";

	return (
		<span className={className}>
			{numeric > 0 ? "+" : ""}
			{numeric.toFixed(1)}%
		</span>
	);
};

const Home = () => {
	const realtimeTick = useAdminRealtimeTick();
	const [analytics, setAnalytics] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");

	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoading(true);
				setError("");
				setAnalytics(await getDashboardAnalytics());
			} catch (err) {
				setError(err?.message || "Не удалось загрузить аналитику.");
			} finally {
				setLoading(false);
			}
		};

		void fetchData();
	}, [realtimeTick]);

	const data = useMemo(() => {
		const stats = analytics?.stats || analytics?.Stats || {};

		return {
			stats,
			kpiCards: kpis(stats),
			sex: (analytics?.sexDistribution || analytics?.SexDistribution || []).map(
				(item) => ({
					name: normalizeGenderName(item.name || item.Name),
					value: toNumber(item.value ?? item.Value),
				}),
			),
			registration: (
				analytics?.registrationTrends ||
				analytics?.RegistrationTrends ||
				[]
			).map(normalizePoint),
			geo: analytics?.geoDistribution || analytics?.GeoDistribution || [],
			revenue: (analytics?.revenue || analytics?.Revenue || []).map(
				normalizePoint,
			),
			userGrowth: (analytics?.userGrowth || analytics?.UserGrowth || []).map(
				normalizePoint,
			),
			segments: (analytics?.segments || analytics?.Segments || []).map(
				normalizeSegment,
			),
		};
	}, [analytics]);

	if (loading) {
		return <div className="home__state">Загружаем аналитику...</div>;
	}

	if (error) {
		return <div className="home__state home__state--error">{error}</div>;
	}

	return (
		<div className="home">
			<header className="home__header">
				<div>
					<h1>Админ аналитика</h1>
				</div>
				<div className="home__headerStats">
					<span>Модерация: {pick(data.stats, "newUsers", "NewUsers")}</span>
					<span>
						Жалобы:{" "}
						{pick(data.stats, "reports_In_Proccess", "Reports_In_Proccess")}
					</span>
					<span>Не заполнены: {pick(data.stats, "failed", "Failed")}</span>
				</div>
			</header>

			<section className="home__segments">
				{segmentOrder.map((key) => {
					const segment = data.segments.find((item) => item.key === key) || {
						total: 0,
						male: 0,
						female: 0,
					};

					return (
						<div className="home__segment" key={key}>
							<span>{segmentLabels[key]}</span>
							<strong>{segment.total}</strong>
							<div>
								<b>М: {segment.male}</b>
								<b>Ж: {segment.female}</b>
							</div>
						</div>
					);
				})}
			</section>

			<section className="home__kpis">
				{data.kpiCards.map((card) => (
					<div className="home__metric" key={card.label}>
						<span>{card.label}</span>
						<strong>{card.value}</strong>
						<Delta value={card.delta} />
						{card.subtitle ? (
							<small className="home__metricSub">{card.subtitle}</small>
						) : null}
					</div>
				))}
			</section>

			<section className="home__layout">
				<div className="home__panel">
					<div className="home__panelHead">
						<h2>Регистрации и удаления</h2>
					</div>
					<ResponsiveContainer width="100%" height={320}>
						<ComposedChart data={data.registration}>
							<CartesianGrid strokeDasharray="3 3" vertical={false} />
							<XAxis dataKey="date" minTickGap={24} />
							<YAxis allowDecimals={false} />
							<Tooltip />
							<Legend />
							<Bar
								dataKey="count"
								name="Регистрации"
								fill="#2563eb"
								radius={[4, 4, 0, 0]}
							/>
							<Bar
								dataKey="delCount"
								name="Удаления"
								fill="#dc2626"
								radius={[4, 4, 0, 0]}
							/>
							<Line
								dataKey="blockCount"
								name="Блокировки"
								stroke="#f59e0b"
								strokeWidth={2}
								dot={false}
							/>
						</ComposedChart>
					</ResponsiveContainer>
				</div>

				<div className="home__panel">
					<div className="home__panelHead">
						<h2>Оплаты</h2>
					</div>
					<ResponsiveContainer width="100%" height={320}>
						<AreaChart data={data.revenue}>
							<CartesianGrid strokeDasharray="3 3" vertical={false} />
							<XAxis dataKey="date" minTickGap={24} />
							<YAxis />
							<Tooltip formatter={(value) => money(value)} />
							<Area
								dataKey="totalRub"
								name="Всего RUB"
								stroke="#16a34a"
								fill="#bbf7d0"
								strokeWidth={2}
							/>
							<Area
								dataKey="rubs"
								name="RUB"
								stroke="#2563eb"
								fill="#bfdbfe"
								strokeWidth={2}
							/>
						</AreaChart>
					</ResponsiveContainer>
				</div>
			</section>

			<section className="home__layout">
				<div className="home__panel">
					<div className="home__panelHead">
						<h2>Пользователи по странам</h2>
					</div>
					<WorldMap data={data.geo} />
				</div>

				<div className="home__panel home__panel--compact">
					<div className="home__panelHead">
						<h2>Пол пользователей</h2>
					</div>
					<ResponsiveContainer width="100%" height={260}>
						<PieChart>
							<Pie
								data={data.sex}
								dataKey="value"
								nameKey="name"
								innerRadius={58}
								outerRadius={92}
								paddingAngle={3}
							>
								{data.sex.map((entry, index) => (
									<Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
								))}
							</Pie>
							<Tooltip />
							<Legend />
						</PieChart>
					</ResponsiveContainer>
				</div>
			</section>

			<section className="home__layout home__layout--wide">
				<div className="home__panel">
					<div className="home__panelHead">
						<h2>Рост пользователей</h2>
					</div>
					<ResponsiveContainer width="100%" height={280}>
						<BarChart data={data.userGrowth}>
							<CartesianGrid strokeDasharray="3 3" vertical={false} />
							<XAxis dataKey="date" minTickGap={24} />
							<YAxis allowDecimals={false} />
							<Tooltip />
							<Bar
								dataKey="users"
								name="Пользователи"
								fill="#7c3aed"
								radius={[4, 4, 0, 0]}
							/>
						</BarChart>
					</ResponsiveContainer>
				</div>
			</section>
		</div>
	);
};

export default Home;
